const EDITED_SUFFIX = ' (editado)'

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi

const HTML_TAG_HINT_REGEX =
  /<\/?(p|br|div|ul|ol|li|strong|em|u|s|del|a|blockquote)\b/i

const DROP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'LINK',
  'META',
  'HEAD',
])

const ALLOWED_TAGS = new Set([
  'A',
  'P',
  'BR',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'S',
  'DEL',
  'UL',
  'OL',
  'LI',
  'BLOCKQUOTE',
])

export type PreparedCommunityMessageContent = {
  html: string
  text: string
}

function normalizeNbsp(input: string): string {
  return String(input || '').replace(/\u00A0/g, ' ').replace(/&nbsp;/gi, ' ')
}

function escapeHtml(text: string): string {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizePlainTextWithLineBreaks(input: string): string {
  const text = normalizeNbsp(input)

  const rawLines = text.split(/\r\n|\r|\n/)

  let start = 0
  let end = rawLines.length
  while (start < end && !rawLines[start].trim()) start += 1
  while (end > start && !rawLines[end - 1].trim()) end -= 1

  const sliced = rawLines.slice(start, end)
  const normalizedLines = sliced.map((line) => line.replace(/[ \t]+/g, ' ').trimEnd())

  return normalizedLines.join('\n').trim()
}

function looksLikeHtml(input: string): boolean {
  const raw = String(input || '')
  return HTML_TAG_HINT_REGEX.test(raw)
}

export function extractCommunityMessagePlainText(input: string): string {
  const raw = normalizeNbsp(input).trim()
  if (!raw) return ''

  if (!/[<>&]/.test(raw)) {
    return normalizePlainTextWithLineBreaks(raw)
  }

  const withBreaks = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote)\s*>/gi, '\n')

  const withoutTags = withBreaks.replace(/<[^>]+>/g, ' ')

  return normalizePlainTextWithLineBreaks(withoutTags)
}

function isInsideAnchor(node: Node): boolean {
  let current: Node | null = node
  while (current) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      (current as HTMLElement).tagName.toUpperCase() === 'A'
    ) {
      return true
    }
    current = current.parentNode
  }
  return false
}

function linkifyTextNodes(doc: Document) {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text)
  }

  textNodes.forEach((textNode) => {
    if (!textNode.parentNode) return
    if (isInsideAnchor(textNode)) return

    const value = textNode.nodeValue || ''
    URL_REGEX.lastIndex = 0
    const matches = Array.from(value.matchAll(URL_REGEX))
    if (!matches.length) return

    const frag = doc.createDocumentFragment()
    let lastIndex = 0

    matches.forEach((match) => {
      const full = match[0]
      const url = match[1] || full
      const index = match.index ?? 0

      if (index > lastIndex) {
        frag.appendChild(doc.createTextNode(value.slice(lastIndex, index)))
      }

      const a = doc.createElement('a')
      a.textContent = url
      a.setAttribute('href', url)
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer nofollow')
      frag.appendChild(a)

      lastIndex = index + full.length
    })

    if (lastIndex < value.length) {
      frag.appendChild(doc.createTextNode(value.slice(lastIndex)))
    }

    try {
      textNode.parentNode.replaceChild(frag, textNode)
    } catch {
      // ignore
    }
  })
}

function sanitizeHtmlKeepingFormatting(input: string): string {
  const raw = normalizeNbsp(input).trim()
  if (!raw) return ''

  if (!looksLikeHtml(raw)) {
    const plain = normalizePlainTextWithLineBreaks(raw)
    const escaped = escapeHtml(plain)
    return escaped.replace(/\n/g, '<br>')
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(raw, 'text/html')

  const walk = (node: Node) => {
    const children = Array.from(node.childNodes)
    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement
        const tag = el.tagName.toUpperCase()

        walk(child)

        if (DROP_TAGS.has(tag)) {
          try {
            el.remove()
          } catch {
            // ignore
          }
          return
        }

        if (!ALLOWED_TAGS.has(tag)) {
          const frag = doc.createDocumentFragment()
          while (el.firstChild) frag.appendChild(el.firstChild)
          try {
            el.replaceWith(frag)
          } catch {
            // ignore
          }
          return
        }

        for (const attr of Array.from(el.attributes)) {
          if (
            tag === 'A' &&
            (attr.name === 'href' || attr.name === 'target' || attr.name === 'rel')
          ) {
            continue
          }
          el.removeAttribute(attr.name)
        }

        if (tag === 'A') {
          const href = String(el.getAttribute('href') || '').trim()
          const safe = /^https?:\/\//i.test(href)
          if (!safe) {
            el.removeAttribute('href')
            el.removeAttribute('target')
            el.removeAttribute('rel')
          } else {
            el.setAttribute('href', href)
            el.setAttribute('target', '_blank')
            el.setAttribute('rel', 'noopener noreferrer nofollow')
          }
        }
      } else {
        walk(child)
      }
    })
  }

  walk(doc.body)
  linkifyTextNodes(doc)

  return String(doc.body.innerHTML || '').trim()
}

export function prepareCommunityMessageContent(input: string): PreparedCommunityMessageContent {
  const html = sanitizeHtmlKeepingFormatting(input)
  const text = extractCommunityMessagePlainText(html)
  return { html, text }
}

export function stripEditedSuffixFromHtml(
  inputHtml: string,
): { html: string; isEdited: boolean } {
  const prepared = prepareCommunityMessageContent(inputHtml)
  if (!prepared.text) return { html: prepared.html, isEdited: false }

  const suffixLower = EDITED_SUFFIX.toLowerCase()
  if (!prepared.text.toLowerCase().endsWith(suffixLower)) {
    return { html: prepared.html, isEdited: false }
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(prepared.html, 'text/html')

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text)
  }

  for (let i = textNodes.length - 1; i >= 0; i -= 1) {
    const t = textNodes[i]
    const value = t.nodeValue || ''
    if (!value.trim()) continue

    const lower = value.toLowerCase()
    if (!lower.endsWith(suffixLower)) continue

    t.nodeValue = value.slice(0, Math.max(0, value.length - EDITED_SUFFIX.length))
    break
  }

  return {
    html: String(doc.body.innerHTML || '').trim(),
    isEdited: true,
  }
}

export function appendEditedSuffixToHtml(inputHtml: string): string {
  const prepared = prepareCommunityMessageContent(inputHtml)
  if (!prepared.text) return ''

  const base = stripEditedSuffixFromHtml(prepared.html).html
  if (!base) return ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(base, 'text/html')

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  let lastText: Text | null = null
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const t = node as Text
    if (!t.nodeValue || !t.nodeValue.trim()) continue
    if (isInsideAnchor(t)) continue
    lastText = t
  }

  if (lastText) {
    lastText.nodeValue = `${lastText.nodeValue || ''}${EDITED_SUFFIX}`
    return String(doc.body.innerHTML || '').trim()
  }

  const suffixNode = doc.createTextNode(EDITED_SUFFIX)
  const lastChild = doc.body.lastChild
  if (lastChild && lastChild.nodeType === Node.ELEMENT_NODE) {
    const el = lastChild as HTMLElement
    if (el.tagName.toUpperCase() !== 'A') {
      el.appendChild(suffixNode)
      return String(doc.body.innerHTML || '').trim()
    }
  }

  doc.body.appendChild(suffixNode)
  return String(doc.body.innerHTML || '').trim()
}

